import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const DataContext = createContext();

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
  const [companies, setCompanies] = useState([]);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [utredere, setUtredere] = useState([]);
  const [internalMessages, setInternalMessages] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [externalServices, setExternalServices] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [guides, setGuides] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [promoOffers, setPromoOffers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [utrederInnboks, setUtrederInnboks] = useState([]);
  const [investigationPricing, setInvestigationPricing] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- VARSLINGSKATEGORIER ---
  const [reportCategories, setReportCategories] = useState({
    mobbing: {
      label: '1. MOBBING, TRAKASSERING OG DISKRIMINERING',
      color: 'purple',
      categories: [
        { id: 'mobbing', name: 'Mobbing' },
        { id: 'utfrysing', name: 'Utfrysing eller utestenging' },
        { id: 'seksuell_trakassering', name: 'Seksuell trakassering' },
        { id: 'diskriminering', name: 'Diskriminering' }
      ]
    },
    arbeidsmiljo: {
      label: '2. KRITIKKVERDIGE FORHOLD I ARBEIDSMILJØET',
      color: 'blue',
      categories: [
        { id: 'fryktkultur', name: 'Fryktkultur eller taushetskultur' },
        { id: 'uforsvarlig_belastning', name: 'Uforsvarlig arbeidsbelastning' }
      ]
    },
    vold: {
      label: '3. VOLD, TRUSLER OG ALVORLIG ADFERD',
      color: 'red',
      categories: [
        { id: 'fysisk_vold', name: 'Fysisk vold' },
        { id: 'trusler', name: 'Trusler' }
      ]
    },
    lovbrudd: {
      label: '5. BRUDD PÅ LOVER OG FORSKRIFTER',
      color: 'cyan',
      categories: [
        { id: 'mulig_lovbrudd', name: 'Mulig lovbrudd' },
        { id: 'brudd_aml', name: 'Brudd på arbeidsmiljøregelverket' }
      ]
    }
  });

  // --- RUH KATEGORIER ---
  const [ruhCategories, setRuhCategories] = useState({
    person: {
      label: 'Personskade / Helse',
      color: 'red',
      categories: [
        { id: 'fall', name: 'Fallulykke' },
        { id: 'kutt', name: 'Kuttskade' }
      ]
    },
    miljo: {
      label: 'Ytre miljø',
      color: 'green',
      categories: [
        { id: 'utslipp', name: 'Utslipp' },
        { id: 'avfall', name: 'Feil avfallshåndtering' }
      ]
    }
  });

  const INVOICE_SENDER = {
    name: "Employer Brand AS",
    address: "Motzfeldts gate 21",
    zip: "0561",
    city: "Oslo",
    orgNr: "925 182 672 MVA",
    bankAccount: "1506.56.12345",
    email: "post@hmsvarsling.no"
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (utredere.length > 0) {
      localStorage.setItem('hms_demo_utredere', JSON.stringify(utredere));
    }
  }, [utredere]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [
        { data: comp },
        { data: rep },
        { data: appUsers },
        { data: part },
        { data: sell },
        { data: utr },
        { data: msgs },
        { data: depts },
        { data: fetchedGuides },
        { data: tickets },
        { data: plans },
        { data: promos },
        { data: extSrv },
        { data: fetchedBookings },
        { data: innboks },
        { data: invs },
        { data: sysSettings }
      ] = await Promise.all([
        supabase.from('companies').select('*'),
        supabase.from('reports').select('*'),
        supabase.from('app_users').select('*'),
        supabase.from('partners').select('*'),
        supabase.from('sellers').select('*'),
        supabase.from('utredere').select('*'),
        supabase.from('internal_messages').select('*').order('created_at', { ascending: true }),
        supabase.from('departments_1770716447012').select('*'),
        supabase.from('guides_internal').select('*'),
        supabase.from('support_tickets').select('*'),
        supabase.from('subscription_plans_1739544000').select('*'),
        supabase.from('promo_offers_1739544000').select('*'),
        supabase.from('external_services').select('*'),
        supabase.from('bookings_1739547000').select('*'),
        supabase.from('utreder_innboks').select('*'),
        supabase.from('invoices').select('*'),
        supabase.from('system_settings_1739548000').select('*')
      ]);

      setCompanies(comp || []);
      setReports(rep || []);
      setUsers(appUsers || []);
      setPartners(part || []);
      setSellers(sell || []);
      
      const savedDemoUtredere = JSON.parse(localStorage.getItem('hms_demo_utredere') || '[]');
      const dbUtredere = utr || [];
      
      const mergedMap = new Map();
      dbUtredere.forEach(u => mergedMap.set(String(u.id), u));
      savedDemoUtredere.forEach(u => mergedMap.set(String(u.id), u));
      
      if (!mergedMap.has('utr_demo_hansen')) {
        mergedMap.set('utr_demo_hansen', {
          id: "utr_demo_hansen", 
          full_name: "Utreder Hansen", 
          company_name: "Hansen Gransking & Analyse", 
          email: "utreder@hansen.no", 
          role: "utreder",
          status: "active",
          active: true,
          geography: "Hele Norge",
          competence_tags: ["Arbeidsmiljø", "Korrupsjon", "HMS-avvik"],
          bio: "Spesialist på komplekse granskinger i privat og offentlig sektor.",
          slug: "hansen-utredning",
          title_role: "Uavhengig Utreder",
          subscription_plan: "pro",
          max_capacity: 5
        });
      }
      
      setUtredere(Array.from(mergedMap.values()));

      setInternalMessages(msgs || []);
      setDepartments(depts || []);
      setGuides(fetchedGuides || []);
      setSupportTickets(tickets || []);
      setSubscriptionPlans(plans || []);
      setPromoOffers(promos || []);
      setExternalServices(extSrv || []);
      setUtrederInnboks(innboks || []);
      setInvoices(invs || []);
      setBookings((fetchedBookings || []).map(b => ({
        ...b,
        customerName: b.customer_name,
        customerEmail: b.customer_email,
        customerCompany: b.customer_company,
        sellerId: b.seller_id
      })));

      if (sysSettings) {
        const pricingSetting = sysSettings.find(s => s.key === 'investigation_pricing');
        if (pricingSetting) setInvestigationPricing(pricingSetting.value);
      }
    } catch (e) {
      console.error("Data fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  const authenticateUser = async (email, password) => {
    if (!email || !password) return null;
    const lowerEmail = email.toLowerCase();
    
    if (lowerEmail === "admin@hmsvarsling.no" && password === "admin123") {
      return { 
        id: "superadmin", 
        email: "admin@hmsvarsling.no", 
        name: "Superadmin", 
        role: "superadmin", 
        isSuperAdmin: true 
      };
    }

    if (lowerEmail === "utreder@hansen.no" && password === "utreder123") {
      const existing = utredere.find(u => u.id === 'utr_demo_hansen');
      if (existing) return { ...existing, role: 'utreder' };
      
      return { 
        id: "utr_demo_hansen", 
        full_name: "Utreder Hansen", 
        company_name: "Hansen Gransking & Analyse", 
        email: "utreder@hansen.no", 
        role: "utreder",
        status: "active",
        active: true,
        geography: "Hele Norge",
        competence_tags: ["Arbeidsmiljø", "Korrupsjon", "HMS-avvik"],
        bio: "Spesialist på komplekse granskinger i privat og offentlig sektor.",
        slug: "hansen-utredning"
      };
    }

    const user = users?.find(u => u.email?.toLowerCase() === lowerEmail && u.password === password);
    if (user) return user;

    const utreder = utredere?.find(u => u.email?.toLowerCase() === lowerEmail && u.password === password);
    if (utreder) return { ...utreder, role: 'utreder' };
    
    return null;
  };

  const updateUtreder = async (id, updates) => {
    setUtredere(prev => {
      const exists = prev.some(u => String(u.id) === String(id));
      let updated;
      if (exists) {
        updated = prev.map(u => 
          String(u.id) === String(id) ? { ...u, ...updates } : u
        );
      } else {
        updated = [...prev, { id, ...updates }];
      }
      localStorage.setItem('hms_demo_utredere', JSON.stringify(updated));
      return updated;
    });
    try {
      await supabase.from('utredere').update(updates).eq('id', id);
      return { success: true };
    } catch (e) {
      return { success: true };
    }
  };

  const updateUtrederProfile = updateUtreder;

  const addUtreder = async (utrederData) => {
    const payload = {
      ...utrederData,
      id: `utr_${Date.now()}`,
      full_name: utrederData.full_name || utrederData.fullName,
      company_name: utrederData.company_name || utrederData.companyName || 'Ikke oppgitt',
      email: utrederData.email,
      password: utrederData.password,
      subscription_plan: utrederData.subscription_plan || 'basic',
      active: false,
      status: 'pending_review',
      roles: ['utreder'],
      created_at: new Date().toISOString(),
      slug: (utrederData.full_name || 'utreder').toLowerCase().replace(/\s+/g, '-') + '-' + Math.floor(Math.random() * 1000)
    };
    try {
      await supabase.from('utredere').insert([payload]);
      setUtredere(prev => [...prev, payload]);
      return { success: true, data: payload };
    } catch (error) {
      setUtredere(prev => [...prev, payload]);
      return { success: true, data: payload };
    }
  };

  const deleteUtreder = async (id) => {
    try {
      await supabase.from('utredere').delete().eq('id', id);
      setUtredere(prev => prev.filter(u => String(u.id) !== String(id)));
      return { success: true };
    } catch (e) {
      setUtredere(prev => prev.filter(u => String(u.id) !== String(id)));
      return { success: true };
    }
  };

  const addCompany = async (companyData) => {
    try {
      const { data, error } = await supabase.from('companies').insert([companyData]).select();
      if (error) throw error;
      setCompanies(prev => [...prev, data[0]]);
      return { success: true, data: data[0] };
    } catch (error) {
      setCompanies(prev => [...prev, companyData]);
      return { success: true, data: companyData };
    }
  };

  const updateCompany = async (id, updates) => {
    try {
      await supabase.from('companies').update(updates).eq('id', id);
      setCompanies(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
      return { success: true };
    } catch (error) {
      setCompanies(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
      return { success: true };
    }
  };

  const addUser = async (userData) => {
    try {
      const { data, error } = await supabase.from('app_users').insert([userData]).select();
      if (error) throw error;
      setUsers(prev => [...prev, data[0]]);
      return { success: true, data: data[0] };
    } catch (error) {
      setUsers(prev => [...prev, userData]);
      return { success: true, data: userData };
    }
  };

  const updateUser = async (id, updates) => {
    try {
      await supabase.from('app_users').update(updates).eq('id', id);
      setUsers(prev => prev.map(u => u.id === id ? { ...u, ...updates } : u));
      return { success: true };
    } catch (e) {
      setUsers(prev => prev.map(u => u.id === id ? { ...u, ...updates } : u));
      return { success: true };
    }
  };

  const deleteUser = async (id) => {
    try {
      await supabase.from('app_users').delete().eq('id', id);
      setUsers(prev => prev.filter(u => u.id !== id));
      return { success: true };
    } catch (e) {
      setUsers(prev => prev.filter(u => u.id !== id));
      return { success: true };
    }
  };

  const addPartner = async (partnerData) => {
    try {
      const { data, error } = await supabase.from('partners').insert([partnerData]).select();
      if (error) return { success: false, error };
      if (!data?.[0]) {
        return {
          success: false,
          error: new Error('Partner insert returned no saved row')
        };
      }
      setPartners(prev => [...prev, data[0]]);
      return { success: true, data: data[0] };
    } catch (error) {
      return { success: false, error };
    }
  };

  const updatePartner = async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('partners')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) return { success: false, error };
      if (!data) {
        return {
          success: false,
          error: new Error('Partner update returned no updated row')
        };
      }
      setPartners(prev => prev.map(p => p.id === id ? data : p));
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const deletePartner = async (id) => {
    try {
      await supabase.from('partners').update({ is_deleted: true }).eq('id', id);
      setPartners(prev => prev.filter(p => p.id !== id));
      return { success: true };
    } catch (e) {
      setPartners(prev => prev.filter(p => p.id !== id));
      return { success: true };
    }
  };

  const addSeller = async (sellerData) => {
    try {
      const { data, error } = await supabase.from('sellers').insert([sellerData]).select();
      if (error) throw error;
      if (!data?.[0]) throw new Error('Seller insert returned no saved row');
      setSellers(prev => [...prev, data[0]]);
      return { success: true, data: data[0] };
    } catch (error) {
      return {
        success: false,
        message: 'Seller registration failed',
        error
      };
    }
  };

  const addReport = async (reportData) => {
    const payload = {
      id: reportData.id || `REP-${Date.now()}`,
      company_id: reportData.companyId,
      title: reportData.title || 'Uten tittel',
      description: reportData.description || '',
      type: reportData.type || 'whistle_report',
      status: 'new',
      created_at: new Date().toISOString()
    };
    try {
      const { data, error } = await supabase.from('reports').insert([payload]).select();
      const final = (data && data[0]) ? data[0] : payload;
      setReports(prev => [final, ...prev]);
      return { success: true, data: final };
    } catch (error) {
      setReports(prev => [payload, ...prev]);
      return { success: true, data: payload };
    }
  };

  const updateReport = async (id, updates) => {
    try {
      await supabase.from('reports').update(updates).eq('id', id);
      setReports(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
      return { success: true };
    } catch (e) {
      setReports(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
      return { success: true };
    }
  };

  const createInvoice = async (company, items) => {
    try {
      const net = items.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
      const payload = {
        company_id: company.id,
        total_amount: net * 1.25,
        net_amount: net,
        vat_amount: net * 0.25,
        status: 'unpaid',
        items: items,
        due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString()
      };
      const { data, error } = await supabase.from('invoices').insert([payload]).select();
      if (error) throw error;
      setInvoices(prev => [...prev, data[0]]);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateInvoiceStatus = async (id, status) => {
    try {
      await supabase.from('invoices').update({ status }).eq('id', id);
      setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, status } : inv));
    } catch (e) {
      setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, status } : inv));
    }
  };

  const addBooking = async (bookingData) => {
    try {
      const { data, error } = await supabase.from('bookings_1739547000').insert([bookingData]).select();
      if (error) throw error;
      setBookings(prev => [...prev, data[0]]);
      return { success: true };
    } catch (error) {
      setBookings(prev => [...prev, bookingData]);
      return { success: true };
    }
  };

  const updateBooking = async (id, updates) => {
    try {
      await supabase.from('bookings_1739547000').update(updates).eq('id', id);
      setBookings(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
      return { success: true };
    } catch (e) {
      setBookings(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
      return { success: true };
    }
  };

  const updateInvestigationPricing = async (newPricing) => {
    try {
      await supabase.from('system_settings_1739548000').upsert({ key: 'investigation_pricing', value: newPricing });
      setInvestigationPricing(newPricing);
    } catch (e) {
      setInvestigationPricing(newPricing);
    }
  };

  const updateSubscriptionPlan = async (id, updates) => {
    try {
      await supabase.from('subscription_plans_1739544000').update(updates).eq('id', id);
      setSubscriptionPlans(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
      return { success: true };
    } catch (e) {
      setSubscriptionPlans(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
      return { success: true };
    }
  };

  const addSubscriptionPlan = async (plan) => {
    try {
      const { data, error } = await supabase.from('subscription_plans_1739544000').insert([plan]).select();
      if (error) throw error;
      setSubscriptionPlans(prev => [...prev, data[0]]);
      return { success: true };
    } catch (e) {
      setSubscriptionPlans(prev => [...prev, plan]);
      return { success: true };
    }
  };

  const deleteSubscriptionPlan = async (id) => {
    try {
      await supabase.from('subscription_plans_1739544000').delete().eq('id', id);
      setSubscriptionPlans(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      setSubscriptionPlans(prev => prev.filter(p => p.id !== id));
    }
  };

  const addPromoOffer = async (promo) => {
    try {
      const { data, error } = await supabase.from('promo_offers_1739544000').insert([promo]).select();
      if (error) throw error;
      setPromoOffers(prev => [...prev, data[0]]);
      return { success: true };
    } catch (e) {
      setPromoOffers(prev => [...prev, promo]);
      return { success: true };
    }
  };

  const updatePromoOffer = async (id, updates) => {
    try {
      await supabase.from('promo_offers_1739544000').update(updates).eq('id', id);
      setPromoOffers(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
      return { success: true };
    } catch (e) {
      setPromoOffers(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
      return { success: true };
    }
  };

  const deletePromoOffer = async (id) => {
    try {
      await supabase.from('promo_offers_1739544000').delete().eq('id', id);
      setPromoOffers(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      setPromoOffers(prev => prev.filter(p => p.id !== id));
    }
  };

  const addExternalService = async (service) => {
    try {
      const { data, error } = await supabase.from('external_services').insert([service]).select();
      if (error) throw error;
      setExternalServices(prev => [...prev, data[0]]);
      return { success: true };
    } catch (e) {
      setExternalServices(prev => [...prev, service]);
      return { success: true };
    }
  };

  const updateExternalService = async (id, updates) => {
    try {
      await supabase.from('external_services').update(updates).eq('id', id);
      setExternalServices(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
      return { success: true };
    } catch (e) {
      setExternalServices(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
      return { success: true };
    }
  };

  const deleteExternalService = async (id) => {
    try {
      await supabase.from('external_services').delete().eq('id', id);
      setExternalServices(prev => prev.filter(s => s.id !== id));
    } catch (e) {
      setExternalServices(prev => prev.filter(s => s.id !== id));
    }
  };

  const addDepartment = async (dept) => {
    try {
      const { data, error } = await supabase.from('departments_1770716447012').insert([dept]).select();
      if (!error) setDepartments(prev => [...prev, data[0]]);
    } catch (e) {
      setDepartments(prev => [...prev, dept]);
    }
  };

  const updateDepartment = async (id, updates) => {
    try {
      await supabase.from('departments_1770716447012').update(updates).eq('id', id);
      setDepartments(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
    } catch (e) {
      setDepartments(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
    }
  };

  const deleteDepartment = async (id) => {
    try {
      await supabase.from('departments_1770716447012').delete().eq('id', id);
      setDepartments(prev => prev.filter(d => d.id !== id));
    } catch (e) {
      setDepartments(prev => prev.filter(d => d.id !== id));
    }
  };

  const addGuide = async (guide) => {
    try {
      const { data, error } = await supabase.from('guides_internal').insert([guide]).select();
      if (!error) setGuides(prev => [...prev, data[0]]);
    } catch (e) {
      setGuides(prev => [...prev, { ...guide, id: Date.now() }]);
    }
  };

  const updateGuide = async (id, updates) => {
    try {
      await supabase.from('guides_internal').update(updates).eq('id', id);
      setGuides(prev => prev.map(g => g.id === id ? { ...g, ...updates } : g));
    } catch (e) {
      setGuides(prev => prev.map(g => g.id === id ? { ...g, ...updates } : g));
    }
  };

  const deleteGuide = async (id) => {
    try {
      await supabase.from('guides_internal').delete().eq('id', id);
      setGuides(prev => prev.filter(g => g.id !== id));
    } catch (e) {
      setGuides(prev => prev.filter(g => g.id !== id));
    }
  };

  const updateSupportTicketStatus = async (id, status) => {
    try {
      await supabase.from('support_tickets').update({ status }).eq('id', id);
      setSupportTickets(prev => prev.map(t => t.id === id ? { ...t, status } : t));
    } catch (e) {
      setSupportTickets(prev => prev.map(t => t.id === id ? { ...t, status } : t));
    }
  };

  const submitSupportTicket = async (ticketData) => {
    try {
      const { data, error } = await supabase.from('support_tickets').insert([ticketData]).select();
      if (!error) setSupportTickets(prev => [...prev, data[0]]);
      return { success: true };
    } catch (error) {
      setSupportTickets(prev => [...prev, ticketData]);
      return { success: true };
    }
  };

  const updateCategories = (newCategories) => {
    setReportCategories(newCategories);
  };

  const updateRuhCategories = (newCategories) => {
    setRuhCategories(newCategories);
  };

  const value = {
    companies,
    reports,
    users,
    partners,
    sellers,
    utredere,
    internalMessages,
    invoices,
    externalServices,
    departments,
    guides,
    supportTickets,
    bookings,
    utrederInnboks,
    loading,
    subscriptionPlans,
    promoOffers,
    reportCategories,
    ruhCategories,
    INVOICE_SENDER,
    investigationPricing,
    authenticateUser,
    addCompany,
    updateCompany,
    addUser,
    updateUser,
    deleteUser,
    addPartner,
    updatePartner,
    deletePartner,
    addSeller,
    addUtreder,
    updateUtreder,
    updateUtrederProfile,
    deleteUtreder,
    createInvoice,
    updateInvoiceStatus,
    addReport,
    updateReport,
    submitSupportTicket,
    updateSupportTicketStatus,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    addBooking,
    updateBooking,
    addSubscriptionPlan,
    updateSubscriptionPlan,
    deleteSubscriptionPlan,
    addPromoOffer,
    updatePromoOffer,
    deletePromoOffer,
    addExternalService,
    updateExternalService,
    deleteExternalService,
    updateInvestigationPricing,
    addGuide,
    updateGuide,
    deleteGuide,
    updateCategories,
    updateRuhCategories,
    incrementPromoUsage: (id) => setPromoOffers(prev => prev.map(p => p.id === id ? { ...p, used_count: (p.used_count || 0) + 1 } : p)),
    getSalesByPartner: (id) => (companies || []).filter(c => String(c.raw_data?.salesAgentId) === String(id) || String(c.salesAgentId) === String(id)),
    getSalesBySeller: (id) => (companies || []).filter(c => String(c.raw_data?.salesAgentId) === String(id) || String(c.salesAgentId) === String(id)),
    getUnreadCount: (id) => (internalMessages || []).filter(m => m.receiver_id === id && !m.read).length
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
